import '#components/complete-the-look'
import './shop-the-look-drawer.css'

type ShopifyProductJSON = any

type CTLProduct = {
  id: number
  handle: string
  title: string
  price: number
  compare_at_price: number | null
  available: boolean
  options: string[]
  variants: Array<{
    id: number
    title: string
    price: number
    compare_at_price: number | null
    available: boolean
    option1: string
    option2: string | null
    option3: string | null
  }>
  featured_image: string
  images: string[]
  tags: string[]
}

const cache = new Map<string, CTLProduct>()

const toNumber = (v: any): number => {
  const n = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : 0
  return Number.isFinite(n) ? n : 0
}

const normalizeOptions = (p: ShopifyProductJSON): string[] => {
  if (Array.isArray(p.options) && typeof p.options[0] === 'string') return p.options
  if (Array.isArray(p.options) && p.options[0]?.name) return p.options.map((o: any) => o.name)
  return []
}

const mapProduct = (p: ShopifyProductJSON, handle: string): CTLProduct => {
  const options = normalizeOptions(p)
  const variants = Array.isArray(p.variants) ? p.variants : []
  const firstVariant = variants[0] || {}

  return {
    id: toNumber(p.id),
    handle,
    title: p.title || '',
    price: toNumber(firstVariant.price ?? p.price ?? 0),
    compare_at_price: (firstVariant.compare_at_price == null ? null : toNumber(firstVariant.compare_at_price)),
    available: Boolean(p.available),
    options,
    variants: variants.map((v: any) => ({
      id: toNumber(v.id),
      title: v.title || '',
      price: toNumber(v.price ?? 0),
      compare_at_price: v.compare_at_price == null ? null : toNumber(v.compare_at_price),
      available: Boolean(v.available),
      option1: v.option1 || '',
      option2: v.option2 ?? null,
      option3: v.option3 ?? null
    })),
    featured_image: p.featured_image || (Array.isArray(p.images) ? p.images[0] : '') || '',
    images: Array.isArray(p.images) ? p.images : [],
    tags: Array.isArray(p.tags) ? p.tags : []
  }
}

async function fetchProductByHandle(handle: string): Promise<CTLProduct | null> {
  const h = handle.trim()
  if (!h) return null
  if (cache.has(h)) return cache.get(h)!

  const res = await fetch(`${window.Shopify.routes.root}products/${h}.js`, {
    credentials: 'same-origin'
  })

  if (!res.ok) return null

  const json = await res.json()
  const mapped = mapProduct(json, h)
  cache.set(h, mapped)
  return mapped
}

class ShopTheLookDrawer extends HTMLElement {
  private overlay!: HTMLDivElement
  private body!: HTMLDivElement
  private closeBtn!: HTMLButtonElement
  private loadingEl!: HTMLDivElement
  private lastFocus: Element | null = null
  private isOpen = false
  private drawerTitle = 'Shop The Look'

  connectedCallback() {
    if (this.dataset.ready === '1') return
    this.dataset.ready = '1'
    this.render()
    document.addEventListener('shop-the-look:open', this.onOpen)
    document.addEventListener('keydown', this.onKeydown)
  }

  disconnectedCallback() {
    document.removeEventListener('shop-the-look:open', this.onOpen)
    document.removeEventListener('keydown', this.onKeydown)
  }

  private render() {
    this.innerHTML = `
      <div class="stl-overlay" part="overlay" aria-hidden="true"></div>
      <div class="stl-panel" part="panel" role="dialog" aria-modal="true" aria-label="Shop the look">
        <div class="tw-relative tw-mb-10">
          <button class="stl-close" type="button" aria-label="Close">×</button>
          <div class="stl-title tw-text-center tw-w-full tw-p-5 tw-border-b tw-border-black tw-uppercase tw-text-lg tw-font-bold">
            ${this.drawerTitle}
          </div>
        </div>
        <div class="stl-body">
          <div class="stl-loading tw-text-center tw-text-lg" hidden>Loading…</div>
          <div class="stl-mount"></div>
        </div>
      </div>
    `

    this.overlay = this.querySelector('.stl-overlay') as HTMLDivElement
    this.body = this.querySelector('.stl-mount') as HTMLDivElement
    this.closeBtn = this.querySelector('.stl-close') as HTMLButtonElement
    this.loadingEl = this.querySelector('.stl-loading') as HTMLDivElement

    this.overlay.addEventListener('click', () => this.close())
    this.closeBtn.addEventListener('click', () => this.close())
  }

  private onKeydown = (e: KeyboardEvent) => {
    if (!this.isOpen) return
    if (e.key === 'Escape') this.close()
  }

  private onOpen = (e: Event) => {
    const ce = e as CustomEvent

    const handles = (ce.detail?.handles || [])
      .map((s: string) => s.trim())
      .filter(Boolean)

    if (!handles.length) return

    this.drawerTitle = ce.detail?.title || 'Shop The Look'

    const titleEl = this.querySelector('.stl-title') as HTMLElement | null
    if (titleEl) {
      titleEl.textContent = this.drawerTitle
    }

    this.open(handles)
  }

  private setLoading(isLoading: boolean) {
    this.loadingEl.hidden = !isLoading
  }

  private async open(handles: string[]) {
    this.lastFocus = document.activeElement
    this.isOpen = true
    document.documentElement.classList.add('stl-open')
    this.overlay.setAttribute('aria-hidden', 'false')
    this.setAttribute('data-open', '1')

    this.setLoading(true)
    this.body.innerHTML = ''

    const products = (await Promise.all(handles.slice(0, 4).map(fetchProductByHandle))).filter(Boolean) as CTLProduct[]

    this.setLoading(false)

    if (!products.length) {
      this.body.innerHTML = `<p class="stl-empty">No products found.</p>`
      return
    }

    const props = encodeURIComponent(JSON.stringify({ products }))
    this.body.innerHTML = `
      <complete-the-look data-is-vue="true" data-props="${props}"></complete-the-look>
    `

    customElements.whenDefined('complete-the-look').then(() => {
      const el = this.body.querySelector('complete-the-look') as HTMLElement | null
      if (el) customElements.upgrade(el)
    })

    requestAnimationFrame(() => this.closeBtn.focus())
  }

  private close() {
    if (!this.isOpen) return
    this.isOpen = false
    document.documentElement.classList.remove('stl-open')
    this.overlay.setAttribute('aria-hidden', 'true')
    this.removeAttribute('data-open')

    this.body.innerHTML = ''

    if (this.lastFocus instanceof HTMLElement) {
      requestAnimationFrame(() => this.lastFocus?.dispatchEvent(new Event('focus')))
    }
    this.lastFocus = null
  }
}

if (!customElements.get('shop-the-look-drawer')) {
  customElements.define('shop-the-look-drawer', ShopTheLookDrawer)
}
